import type { Location, LocationDescriptorObject } from "history";
import defaults from "lodash/defaults";
import isFunction from "lodash/isFunction";
import pick from "lodash/pick";
import qs from "query-string";

import { isArray } from "~/tools/is-array";
import { isPresent } from "~/tools/is-present";

import {
	type AllSearchParams,
	STICKY_SEARCH_PARAMS,
	decodeAllSearchParams,
	encodeAllSearchParams,
} from "../../../app/state/route/SearchParams";
import { generateExploreSubviewQuery, isExploreSubview } from "../../../app/state/view/viewpoint/ViewPointQueryUtils";
import { generatePath, matchPath } from "../../../generic/routing";

import { type ExploreSubviewSource, extractExploreSubviewFromSource } from "./ExploreSubviewSource";
import type { NavigationMiddleware } from "./NavigationMiddleware";
import type { ExtractPathParams, Reducer } from "./types";

type PathParamsReducer<TRoute extends string, TPrevIsDefined extends boolean = true> = Reducer<
	ExtractPathParams<TRoute>,
	ExtractPathParams<TRoute, { parseParamValues: false }> | (TPrevIsDefined extends true ? never : undefined)
>;

export interface NavigationSearchParams extends Omit<AllSearchParams, "explore"> {
	/**
	 * The explore subview of a view.
	 * It encourages to use the `ExploreSubviewSource` instead of the `ExploreSubview`,
	 * because it has a special meaning for the navigation strategy.
	 *
	 * Every time when a `explore` is passed as `ExploreSubviewSource`, an url generates as follows:
	 * 1. If the path leads to a view __without__ the `?explore` param, then the `explore: ExploreSubviewSource` becomes the `?explore: ExploreSubview` param;
	 * 2. If the path leads to a view __with__ the `?explore` param, then the `explore: ExploreSubviewSource` becomes the `?query: string` param, and the current `?explore: ExploreSubview` param keeps the same.
	 * 3. If the path leads to an explore view (it never has `?explore`), then the `explore: ExploreSubviewSource` becomes the `?query: string` param;
	 *
	 * It is necessary to store the initial view's subview separately, so later it can reset the chained subview.
	 *
	 * ---
	 *
	 * The `ExploreSubview` can be used only for cases when it should pass the current value.
	 * The following example will pass `explore` as `ExploreSubview` if it present:
	 * ```
	 * // if the current url is `/view?explore=...` then the new url will be `/view?explore=...&timestamp=123`
	 * // if the current url is `/view` then the new url will be `/view?timestamp=123`
	 * useNavigateFrom("*", {
	 *  search: (search) => ({ ...search, timestamp: 123 })
	 * })
	 * ```
	 *
	 * In other cases, it is recommended to use the `ExploreSubviewSource`. For example:
	 * ```
	 * useNavigateFrom("*", {
	 *  search: (search) => ({
	 *    ...search,
	 *    explore: { type: "GROUP", groupId: 1 }
	 *  })
	 * })
	 * ```
	 */
	explore?: AllSearchParams["explore"] | ExploreSubviewSource;
}

export interface NavigateWithinOptions {
	/**
	 * The search of a location.
	 * It can be one of the following:
	 * 1. `NavigationSearchParams` - the search will be replaced with the given value;
	 * 2. `(current: NavigationSearchParams) => NavigationSearchParams` - the search will be replaced with the result of the given function where the current search is passed as an argument;
	 * 3. `Array<keyof NavigationSearchParams>` - the search will keep only the given keys;
	 *
	 * If not defined, the search will be empty.
	 */
	search?: Array<keyof NavigationSearchParams> | Reducer<NavigationSearchParams>;
}

interface NavigateWithinContext {
	/**
	 * current location, it is used to calculate the next pathname and search
	 */
	location: Location | LocationDescriptorObject;
	/**
	 * a desired location.pathname excluding the search
	 */
	pathname: string;
	/**
	 * an array of middleware functions that applies before encoding the search params
	 * useful when you want to apply some logic based on analysis of the current and previous pathname/search params
	 */
	middleware: Array<NavigationMiddleware>;
}

const applySearchReducer = (
	current: AllSearchParams,
	search: NavigateWithinOptions["search"],
): NavigationSearchParams => {
	if (typeof search === "function") {
		return { ...pick(current, [...STICKY_SEARCH_PARAMS]), ...search(current) };
	}

	if (isArray(search)) {
		return pick(current, [...search, ...STICKY_SEARCH_PARAMS]);
	}

	return search ?? current;
};

const applyExploreSubviewSource = (currentSearch: AllSearchParams, search: NavigationSearchParams): AllSearchParams => {
	if (search.explore == null) {
		// should delete the originView and originQuery as soon as not in the explore subview
		return {
			...search,
			explore: undefined,
			originView: undefined,
			originQuery: undefined,
		};
	}

	if (isExploreSubview(search.explore)) {
		return { ...search, explore: search.explore };
	}

	const explore = extractExploreSubviewFromSource(search.explore);

	if (explore == null) {
		return { ...search, explore: undefined };
	}

	// if a view didn't have `?explore` - apply the `explore` as a new `?explore` with the `?view` as the `?originView`
	if (currentSearch.explore == null) {
		return {
			query: undefined,
			view: undefined,
			...search,
			explore,
			originView: currentSearch.view,
			originQuery: currentSearch.query,
		};
	}

	// otherwise, keep the current `?explore` and convert the new `explore` to a query so it is exploring the initial `?explore`
	return {
		...search,
		explore: currentSearch.explore,
		query: generateExploreSubviewQuery(explore),
	};
};

/**
 * The function concatenates the given path with the modified search.
 * It detects the current view and modifies the search accordingly (diff only).
 *
 * It navigates "within" because it does not change the pathname but only the search.
 *
 * @returns the path with the modified search.
 */
const navigateWithinLink = (
	{ location, pathname, middleware }: NavigateWithinContext,
	options: NavigateWithinOptions,
) => {
	// the pathname might potentially have a query string
	const [path, query] = pathname.split("?");
	const current = {
		pathname: location.pathname ?? "",
		search: defaults(
			{},
			decodeAllSearchParams({ search: query }, { decodeOnlyPresentParams: true }),
			decodeAllSearchParams(location),
		),
	};
	const initialSearch = applyExploreSubviewSource(current.search, applySearchReducer(current.search, options.search));
	const next = middleware.reduce((acc, fn) => fn(acc, current) ?? acc, {
		pathname: path,
		search: initialSearch,
	});
	const encodedQuery = encodeAllSearchParams(next.search);
	const nextSearch = qs.stringify(encodedQuery ?? {}, {
		encode: true, // should encode search params, otherwise it might contain dangerous characters
	});

	if (!nextSearch) {
		return next.pathname;
	}

	return `${next.pathname}?${nextSearch}`;
};

export interface NavigateToFromContext extends Pick<NavigateWithinContext, "middleware"> {
	location: Location | LocationDescriptorObject;
}

export type NavigateToOptions<TRoute extends string> = NavigateWithinOptions &
	(TRoute extends `${string}:${string}`
		? {
				/**
				 * The params of a location.
				 * It can be one of the following:
				 * 1. `ExtractPathParams<TRoute>` - the params will be replaced with the given value;
				 * 2. `(current: null | ExtractPathParams<TRoute>) => ExtractPathParams<TRoute>` - the params will be replaced with the result of the given function where the current params are passed as an argument;
				 */
				params: PathParamsReducer<TRoute, false>;
			}
		: {
				params?: never;
			});

export const navigateToLink = <TRoute extends string>(
	context: NavigateToFromContext,
	route: TRoute,
	options: NavigateToOptions<TRoute>,
): string => {
	if (options.params == null && options.search == null) {
		return route;
	}

	// no params provided - the route is the same
	if (options.params == null) {
		return navigateWithinLink(
			{
				pathname: route,
				...context,
			},
			options,
		);
	}

	const match = matchPath(context.location.pathname ?? "", { path: route });
	const prevParams = match?.params;
	const params =
		typeof options.params === "function"
			? (options.params as (param: unknown) => never)(prevParams)
			: (options.params ?? prevParams);

	return navigateWithinLink(
		{
			pathname: generatePath(route, params as never),
			...context,
		},
		options,
	);
};

export interface NavigateFromOptions<TRoute extends string> extends NavigateWithinOptions {
	/**
	 * The params of a location. IF a route has only optional or no params, the params can be omitted.
	 * It can be one of the following:
	 * 1. `ExtractPathParams<TRoute>` - the params will be replaced with the given value;
	 * 2. `(current: ExtractPathParams<TRoute>) => ExtractPathParams<TRoute>` - the params will be replaced with the result of the given function where the current params are passed as an argument;
	 *
	 * If not defined, previous params will be used.
	 */
	params?: PathParamsReducer<TRoute>;
}

export const navigateFromLink = <TRoute extends string>(
	context: NavigateToFromContext,
	routeOrRoutes: TRoute | Array<TRoute>,
	options: NavigateFromOptions<TRoute>,
): TRoute extends "*" ? string : void | string => {
	// wildcard - navigate within the current route
	if (routeOrRoutes === "*") {
		return navigateWithinLink(
			{
				pathname: context.location.pathname ?? "",
				...context,
			},
			options,
		);
	}

	const allRoutes = isArray(routeOrRoutes) ? routeOrRoutes : [routeOrRoutes];
	const match = allRoutes.map((route) => matchPath(context.location.pathname ?? "", { path: route })).find(isPresent);

	// no routes matched - nothing to navigate from
	if (match == null) {
		return undefined!;
	}

	const prevParams = match.params;

	const params = isFunction(options.params) ? options.params(prevParams) : (options.params ?? prevParams);

	return navigateWithinLink(
		{
			pathname: generatePath(match.path, params as never),
			...context,
		},
		options,
	);
};
